"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.ButtonWithForwardRef = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _propTypes2 = _interopRequireDefault(require("@styled-system/prop-types"));

var _icon = _interopRequireDefault(require("../icon"));

var _button = _interopRequireWildcard(require("./button.style"));

var _tags = _interopRequireDefault(require("../../utils/helpers/tags"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function () { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function renderChildren({
  /* eslint-disable react/prop-types */
  iconType,
  iconPosition,
  size,
  subtext,
  children,
  disabled,
  buttonType,
  iconTooltipMessage,
  iconTooltipPosition
  /* eslint-enable */

}) {
  const iconColorMap = {
    primary: "on-dark-background",
    secondary: "business-color",
    tertiary: "business-color",
    darkBackground: "business-color"
  };
  const iconProps = {
    type: iconType,
    disabled,
    bgTheme: "none",
    iconColor: iconColorMap[buttonType]
  };
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, iconType && iconPosition === "before" && children && /*#__PURE__*/_react.default.createElement(_icon.default, iconProps), /*#__PURE__*/_react.default.createElement("span", null, /*#__PURE__*/_react.default.createElement("span", {
    "data-element": "main-text"
  }, children), size === "large" && /*#__PURE__*/_react.default.createElement(_button.StyledButtonSubtext, {
    "data-element": "subtext"
  }, subtext)), iconType && !children && /*#__PURE__*/_react.default.createElement(_icon.default, _extends({}, iconProps, {
    tooltipMessage: iconTooltipMessage,
    tooltipPosition: iconTooltipPosition
  })), iconType && iconPosition === "after" && children && /*#__PURE__*/_react.default.createElement(_icon.default, iconProps));
}

const renderStyledButton = buttonProps => {
  const {
    "aria-label": ariaLabel,
    disabled,
    buttonType,
    iconType,
    theme,
    href,
    ref,
    px,
    size,
    noWrap,
    tooltipMessage,
    target,
    rel,
    ...rest
  } = buttonProps;
  let paddingX;

  const handleLinkKeyDown = event => {
    // If space key click link
    if (event.key === " ") {
      event.preventDefault();
      ref.current.click();
    }
  };

  if (href) {
    rest.href = href;
  }

  switch (size) {
    case "small":
      paddingX = 2;
      break;

    case "large":
      paddingX = 4;
      break;

    default:
      paddingX = 3;
  }

  return /*#__PURE__*/_react.default.createElement(_button.default, _extends({
    "aria-label": !rest.children && iconType ? ariaLabel || iconType : undefined,
    as: !disabled && href ? "a" : "button",
    onKeyDown: href && handleLinkKeyDown,
    draggable: false,
    buttonType: buttonType,
    disabled: disabled,
    role: "button",
    type: href ? undefined : "button",
    iconType: iconType,
    size: size,
    px: px || paddingX,
    noWrap: noWrap,
    iconOnly: !rest.children && iconType,
    target: target,
    rel: rel
  }, (0, _tags.default)("button", buttonProps), rest, {
    ref: ref
  }), renderChildren(buttonProps));
};

const Button = props => {
  const {
    size,
    subtext
  } = props;
  const linkRef = (0, _react.useRef)(null);
  const {
    as,
    buttonType,
    forwardRef,
    ...rest
  } = props;
  const propsWithoutAs = { ...rest,
    buttonType: buttonType || as,
    ref: forwardRef || linkRef
  };

  if (subtext.length > 0 && size !== "large") {
    throw new Error("subtext prop has no effect unless the button is large");
  }

  return renderStyledButton(propsWithoutAs);
};

Button.propTypes = {
  /** Styled system spacing props */
  ..._propTypes2.default.space,

  /** Prop to specify the aria-label text. Only to be used in Button when only an icon is rendered. This is required to comply with WCAG 4.1.2 - Buttons must have discernible text  */
  "aria-label": _propTypes.default.string,

  /** Color variants for new business themes: "primary" | "secondary" | "tertiary" | "dashed" | "darkBackground" */
  buttonType: _propTypes.default.oneOf(["primary", "secondary", "tertiary", "dashed", "darkBackground"]),

  /** The text the button displays */
  children: (props, propName, ...rest) => {
    if (!props.iconType && !props.children) {
      return new Error("Either prop `iconType` must be defined or this node must have children.");
    }

    return _propTypes.default.node(props, propName, ...rest);
  },

  /** Apply disabled state to the button */
  disabled: _propTypes.default.bool,

  /** Apply destructive style to the button */
  destructive: _propTypes.default.bool,

  /** Defines an Icon position related to the children: "before" | "after" */
  iconPosition: _propTypes.default.oneOf(["before", "after"]),

  /**
   * <a href="https://brand.sage.com/d/NdbrveWvNheA/foundations#/icons/icons" target="_blank">List of supported icons</a>
   *
   * Defines an Icon type within the button (see Icon for options)
   * */
  iconType: (props, propName, ...rest) => {
    if (!props.iconType && !props.children) {
      return new Error("Either prop `iconType` must be defined or this node must have children.");
    }

    return _propTypes.default.node(props, propName, ...rest);
  },

  /** Assigns a size to the button: "small" | "medium" | "large" */
  size: _propTypes.default.oneOf(["small", "medium", "large"]),

  /** Second text child, renders under main text, only when size is "large" */
  subtext: _propTypes.default.string,

  /** Ref to be forwarded */
  forwardRef: _propTypes.default.oneOfType([_propTypes.default.object, _propTypes.default.func]),

  /** Button types for legacy theme: "primary" | "secondary" */
  as: _propTypes.default.oneOf(["primary", "secondary"]),

  /** Used to transform button into anchor */
  href: _propTypes.default.string,

  /** Apply fullWidth style to the button */
  fullWidth: _propTypes.default.bool,

  /** If provided, the text inside a button will not wrap */
  noWrap: _propTypes.default.bool,

  /** Specify a callback triggered on blur */
  onBlur: _propTypes.default.func,

  /** Specify a callback triggered on change */
  onChange: _propTypes.default.func,

  /** Specify a callback triggered on click */
  onClick: _propTypes.default.func,

  /** Specify a callback triggered on focus */
  onFocus: _propTypes.default.func,

  /** Specify a callback triggered on keyDown */
  onKeyDown: _propTypes.default.func,

  /** Provides a tooltip message when the icon is hovered. */
  iconTooltipMessage: _propTypes.default.string,

  /** Provides positioning when the tooltip is displayed. */
  iconTooltipPosition: _propTypes.default.oneOf(["top", "bottom", "left", "right"]),

  /** HTML button type property */
  type: _propTypes.default.string,

  /** HTML target attribute */
  target: _propTypes.default.string,

  /** HTML rel attribute */
  rel: _propTypes.default.string
};
Button.defaultProps = {
  as: "secondary",
  size: "medium",
  fullWidth: false,
  disabled: false,
  destructive: false,
  iconPosition: "before",
  subtext: ""
};

const ButtonWithForwardRef = /*#__PURE__*/_react.default.forwardRef((props, ref) => /*#__PURE__*/_react.default.createElement(Button, _extends({
  forwardRef: ref
}, props)));

exports.ButtonWithForwardRef = ButtonWithForwardRef;
ButtonWithForwardRef.displayName = "Button";
ButtonWithForwardRef.defaultProps = Button.defaultProps;
Button.displayName = "Button";
var _default = Button;
exports.default = _default;